import React, { createContext, useCallback, useContext, useEffect, useState, ReactNode } from 'react';
import {
  addCartLine,
  createCart,
  getCart,
  getVariantBySku,
  removeCartLine,
  updateCartLine,
  updateCartDiscountCodes,
  type LineAttribute,
} from '@/lib/shopify';

export interface BundleMeta {
  bundleId: string;      // slug of the bundle
  bundleTitle: string;
  bundleImage?: string;
  bundlePrice: number;   // final display price of ONE bundle unit
  bundleUnitQty: number; // always 1 for the first pass — bundles are atomic
  bundleCode?: string;   // discount code to apply on cart
}

export interface CartItem {
  id: string;
  name: string;
  price: number;
  quantity: number;
  image: string;
  /** Shopify cart line ID (null for local-only fallback items). */
  lineId?: string | null;
  /** Shopify variant ID (null for local-only fallback items). */
  variantId?: string | null;
  /** If set, this line is part of a bundle group. */
  bundle?: BundleMeta;
}

export interface BundleInput {
  slug: string;
  title: string;
  image?: string;
  price: number;
  compareAt?: number;
  discountCode?: string;
  components: Array<{ id: string; name: string; price: number; image: string; quantity?: number }>;
}

interface CartContextType {
  cart: CartItem[];
  addToCart: (item: Omit<CartItem, 'lineId' | 'variantId' | 'bundle'>) => Promise<void>;
  addBundle: (bundle: BundleInput) => Promise<void>;
  removeFromCart: (id: string) => Promise<void>;
  removeBundle: (bundleId: string) => Promise<void>;
  updateQuantity: (id: string, quantity: number) => Promise<void>;
  clearCart: () => void;
  cartTotal: number;
  cartCount: number;
  isCartOpen: boolean;
  openCart: () => void;
  closeCart: () => void;
  toggleCart: () => void;
  checkoutUrl: string | null;
  goToCheckout: () => void;
  isLoading: boolean;
  /** Grouped view: standalone items + one entry per bundle (with its components). */
  cartGroups: CartGroup[];
}

export type CartGroup =
  | { type: 'item'; item: CartItem }
  | {
      type: 'bundle';
      bundleId: string;
      bundleTitle: string;
      bundleImage?: string;
      bundlePrice: number;
      bundleCode?: string;
      items: CartItem[];
    };

interface StoredCart {
  items: CartItem[];
  cartId: string | null;
  checkoutUrl: string | null;
}

const STORAGE_KEY = 'riham-cart-v2';

const CartContext = createContext<CartContextType | undefined>(undefined);

function bundleAttributes(meta: BundleMeta): LineAttribute[] {
  const attrs: LineAttribute[] = [
    { key: '_bundle_id', value: meta.bundleId },
    { key: '_bundle_title', value: meta.bundleTitle },
    { key: '_bundle_price', value: String(meta.bundlePrice) },
    { key: '_bundle_unit_qty', value: String(meta.bundleUnitQty) },
  ];
  if (meta.bundleImage) attrs.push({ key: '_bundle_image', value: meta.bundleImage });
  if (meta.bundleCode) attrs.push({ key: '_bundle_code', value: meta.bundleCode });
  return attrs;
}

export const CartProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [cart, setCart] = useState<CartItem[]>([]);
  const [cartId, setCartId] = useState<string | null>(null);
  const [checkoutUrl, setCheckoutUrl] = useState<string | null>(null);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Hydrate from localStorage
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const data = JSON.parse(saved) as StoredCart;
        setCart(data.items ?? []);
        setCartId(data.cartId ?? null);
        setCheckoutUrl(data.checkoutUrl ?? null);
      }
    } catch (e) {
      console.error('Failed to load cart from storage', e);
    }
  }, []);

  useEffect(() => {
    const data: StoredCart = { items: cart, cartId, checkoutUrl };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  }, [cart, cartId, checkoutUrl]);

  const clearCart = useCallback(() => {
    setCart([]);
    setCartId(null);
    setCheckoutUrl(null);
  }, []);

  // Sync cart with Shopify on mount + when tab becomes visible
  useEffect(() => {
    const sync = async () => {
      if (!cartId) return;
      try {
        const c = await getCart(cartId);
        if (!c || c.totalQuantity === 0) clearCart();
      } catch (e) {
        console.error('Cart sync failed', e);
      }
    };
    sync();
    const onVis = () => {
      if (document.visibilityState === 'visible') sync();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartId]);

  /**
   * Recompute unique bundle codes from cart and push them to Shopify.
   * Passing the "next" cart snapshot lets us call this right after state updates.
   */
  const reconcileBundleDiscounts = useCallback(
    async (targetCart: CartItem[], targetCartId: string | null) => {
      if (!targetCartId) return;
      const codes = Array.from(
        new Set(
          targetCart
            .map((i) => i.bundle?.bundleCode)
            .filter((c): c is string => !!c && c.trim().length > 0),
        ),
      );
      const r = await updateCartDiscountCodes(targetCartId, codes);
      if ('cartNotFound' in r) clearCart();
    },
    [clearCart],
  );

  const addToCart = useCallback(
    async (item: Omit<CartItem, 'lineId' | 'variantId' | 'bundle'>) => {
      const reg = getVariantBySku(item.id);
      const variantId = reg?.variantId;
      setIsLoading(true);

      try {
        // Merge only with existing NON-bundle line of same product
        const existing = cart.find((c) => c.id === item.id && !c.bundle);

        if (existing) {
          const newQty = existing.quantity + item.quantity;
          if (cartId && existing.lineId) {
            const r = await updateCartLine(cartId, existing.lineId, newQty);
            if ('cartNotFound' in r) {
              clearCart();
              return;
            }
          }
          setCart((prev) =>
            prev.map((c) => (c.id === item.id && !c.bundle ? { ...c, quantity: newQty } : c)),
          );
          return;
        }

        if (variantId) {
          let lineId: string | undefined;
          if (!cartId) {
            const r = await createCart(variantId, item.quantity);
            if (!r) return;
            setCartId(r.cartId);
            setCheckoutUrl(r.checkoutUrl);
            lineId = r.lineId;
          } else {
            const r = await addCartLine(cartId, variantId, item.quantity);
            if ('cartNotFound' in r) {
              const created = await createCart(variantId, item.quantity);
              if (!created) {
                clearCart();
                return;
              }
              setCart([]);
              setCartId(created.cartId);
              setCheckoutUrl(created.checkoutUrl);
              lineId = created.lineId;
            } else {
              if (!r.success) return;
              lineId = r.lineId;
            }
          }
          setCart((prev) => [...prev, { ...item, lineId: lineId ?? null, variantId }]);
        } else {
          console.warn(`[Cart] No Shopify variant registered for "${item.id}" — adding locally only.`);
          setCart((prev) => [...prev, { ...item, lineId: null, variantId: null }]);
        }
      } finally {
        setIsLoading(false);
      }
    },
    [cart, cartId, clearCart],
  );

  const addBundle = useCallback(
    async (bundle: BundleInput) => {
      if (cart.some((c) => c.bundle?.bundleId === bundle.slug)) {
        // Bundle already in cart — atomic, do not duplicate
        return;
      }

      const meta: BundleMeta = {
        bundleId: bundle.slug,
        bundleTitle: bundle.title,
        bundleImage: bundle.image,
        bundlePrice: bundle.price,
        bundleUnitQty: 1,
        bundleCode: bundle.discountCode,
      };
      const attrs = bundleAttributes(meta);

      setIsLoading(true);
      try {
        const newLines: CartItem[] = [];
        let workingCartId = cartId;
        let workingCheckoutUrl = checkoutUrl;

        for (const comp of bundle.components) {
          const reg = getVariantBySku(comp.id);
          const variantId = reg?.variantId;
          const qty = comp.quantity ?? 1;

          if (!variantId) {
            console.warn(`[Cart] Bundle component "${comp.id}" has no Shopify variant — skipping in Shopify sync.`);
            newLines.push({
              id: comp.id,
              name: comp.name,
              price: comp.price,
              quantity: qty,
              image: comp.image,
              lineId: null,
              variantId: null,
              bundle: meta,
            });
            continue;
          }

          let lineId: string | undefined;
          if (!workingCartId) {
            const r = await createCart(variantId, qty, attrs);
            if (!r) continue;
            workingCartId = r.cartId;
            workingCheckoutUrl = r.checkoutUrl;
            lineId = r.lineId;
          } else {
            const r = await addCartLine(workingCartId, variantId, qty, attrs);
            if ('cartNotFound' in r) {
              const created = await createCart(variantId, qty, attrs);
              if (!created) continue;
              workingCartId = created.cartId;
              workingCheckoutUrl = created.checkoutUrl;
              lineId = created.lineId;
              // Purge stale local cart when Shopify cart was lost
              setCart([]);
            } else if (r.success) {
              lineId = r.lineId;
            } else {
              continue;
            }
          }

          newLines.push({
            id: comp.id,
            name: comp.name,
            price: comp.price,
            quantity: qty,
            image: comp.image,
            lineId: lineId ?? null,
            variantId,
            bundle: meta,
          });
        }

        if (workingCartId !== cartId) setCartId(workingCartId);
        if (workingCheckoutUrl !== checkoutUrl) setCheckoutUrl(workingCheckoutUrl);

        const nextCart = [...cart, ...newLines];
        setCart(nextCart);

        // Push discount codes to Shopify
        await reconcileBundleDiscounts(nextCart, workingCartId);
      } finally {
        setIsLoading(false);
      }
    },
    [cart, cartId, checkoutUrl, reconcileBundleDiscounts],
  );

  const removeFromCart = useCallback(
    async (id: string) => {
      const item = cart.find((c) => c.id === id);
      if (!item) return;

      if (cartId && item.lineId) {
        const r = await removeCartLine(cartId, item.lineId);
        if ('cartNotFound' in r) {
          clearCart();
          return;
        }
      }

      const next = cart.filter((c) => c !== item);
      setCart(next);
      if (next.length === 0) {
        setCartId(null);
        setCheckoutUrl(null);
      }
    },
    [cart, cartId, clearCart],
  );

  const removeBundle = useCallback(
    async (bundleId: string) => {
      const bundleLines = cart.filter((c) => c.bundle?.bundleId === bundleId);
      if (bundleLines.length === 0) return;

      setIsLoading(true);
      try {
        if (cartId) {
          for (const l of bundleLines) {
            if (!l.lineId) continue;
            const r = await removeCartLine(cartId, l.lineId);
            if ('cartNotFound' in r) {
              clearCart();
              return;
            }
          }
        }
        const next = cart.filter((c) => c.bundle?.bundleId !== bundleId);
        setCart(next);
        if (next.length === 0) {
          setCartId(null);
          setCheckoutUrl(null);
        } else {
          // Reconcile discount codes — the removed bundle's code should disappear
          await reconcileBundleDiscounts(next, cartId);
        }
      } finally {
        setIsLoading(false);
      }
    },
    [cart, cartId, clearCart, reconcileBundleDiscounts],
  );

  const updateQuantity = useCallback(
    async (id: string, quantity: number) => {
      if (quantity <= 0) {
        await removeFromCart(id);
        return;
      }
      const item = cart.find((c) => c.id === id && !c.bundle);
      if (!item) return; // Bundle lines are atomic — qty changes ignored

      if (cartId && item.lineId) {
        const r = await updateCartLine(cartId, item.lineId, quantity);
        if ('cartNotFound' in r) {
          clearCart();
          return;
        }
      }
      setCart((prev) =>
        prev.map((c) => (c.id === id && !c.bundle ? { ...c, quantity } : c)),
      );
    },
    [cart, cartId, clearCart, removeFromCart],
  );

  const goToCheckout = useCallback(() => {
    if (checkoutUrl) {
      window.open(checkoutUrl, '_blank');
    }
  }, [checkoutUrl]);

  // Compute display cartTotal: standalone lines at price*qty, bundles at bundlePrice each
  const seenBundles = new Set<string>();
  let cartTotal = 0;
  for (const item of cart) {
    if (item.bundle) {
      if (!seenBundles.has(item.bundle.bundleId)) {
        seenBundles.add(item.bundle.bundleId);
        cartTotal += item.bundle.bundlePrice * item.bundle.bundleUnitQty;
      }
    } else {
      cartTotal += item.price * item.quantity;
    }
  }
  const cartCount = cart.reduce((c, i) => c + i.quantity, 0);

  // Group for UI rendering
  const cartGroups: CartGroup[] = (() => {
    const groups: CartGroup[] = [];
    const bundleIdx = new Map<string, number>();
    for (const item of cart) {
      if (item.bundle) {
        const bid = item.bundle.bundleId;
        if (bundleIdx.has(bid)) {
          const idx = bundleIdx.get(bid)!;
          (groups[idx] as any).items.push(item);
        } else {
          bundleIdx.set(bid, groups.length);
          groups.push({
            type: 'bundle',
            bundleId: bid,
            bundleTitle: item.bundle.bundleTitle,
            bundleImage: item.bundle.bundleImage,
            bundlePrice: item.bundle.bundlePrice,
            bundleCode: item.bundle.bundleCode,
            items: [item],
          });
        }
      } else {
        groups.push({ type: 'item', item });
      }
    }
    return groups;
  })();

  return (
    <CartContext.Provider
      value={{
        cart,
        addToCart,
        addBundle,
        removeFromCart,
        removeBundle,
        updateQuantity,
        clearCart,
        cartTotal,
        cartCount,
        isCartOpen,
        openCart: () => setIsCartOpen(true),
        closeCart: () => setIsCartOpen(false),
        toggleCart: () => setIsCartOpen((v) => !v),
        checkoutUrl,
        goToCheckout,
        isLoading,
        cartGroups,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = (): CartContextType => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within a CartProvider');
  return ctx;
};
